/**
 * Portions of this file come from the MIT-licensed "catch links" Gatsby plugin.
 * See: https://github.com/gatsbyjs/gatsby/tree/master/packages/gatsby-plugin-catch-links
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Gatsbyjs
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export const isModifiedClick = (event) =>
  event.button !== 0 ||
  event.altKey ||
  event.ctrlKey ||
  event.metaKey ||
  event.shiftKey;

// IE does not include leading slash in anchor.pathname
export const slashedPathname = (pathname) =>
  pathname[0] === '/' ? pathname : `/${pathname}`;

export const findClosestAnchor = (node) => {
  for (; node.parentNode; node = node.parentNode) {
    if (node.nodeName.toLowerCase() === 'a') {
      return node;
    }
  }

  return null;
};

export const anchorsTargetIsEquivalentToSelf = (anchor) =>
  // If target attribute is not present it's treated as _self
  !anchor.hasAttribute('target') ||
  // The browser defaults to _self, but, not all browsers set
  // a.target to the string value `_self` by default

  // Assumption: some browsers use null/undefined for default
  // attribute values
  anchor.target == null ||
  // Some browsers use the empty string to mean _self, check
  // for actual `_self`
  anchor.target === '_self' ||
  anchor.target === '' ||
  // As per https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
  (anchor.target === '_parent' &&
    (!anchor.ownerDocument.defaultView.parent || // Assumption: This can be falsey
      anchor.ownerDocument.defaultView.parent ===
        anchor.ownerDocument.defaultView)) ||
  (anchor.target === '_top' &&
    (!anchor.ownerDocument.defaultView.top || // Assumption: This can be falsey
      anchor.ownerDocument.defaultView.top ===
        anchor.ownerDocument.defaultView));

export const isAuthorForcingNavigation = (anchor) =>
  // HTML5 attribute that informs the browser to handle the
  // href as a downloadable file; let the browser handle it
  anchor.hasAttribute('download') ||
  // Let the browser handle anything that doesn't look like a
  // target="_self" anchor
  !anchorsTargetIsEquivalentToSelf(anchor);

// https://developer.mozilla.org/en-US/docs/Web/Security/Same-origin_policy
export const urlsAreOnSameOrigin = (origin, destination) =>
  origin.protocol === destination.protocol &&
  // a.host includes both hostname and port in the expected format host:port
  origin.host === destination.host;

export const hashShouldBeFollowed = (origin, destination) =>
  destination.hash !== '' &&
  // Dynamically created anchor links (href="#my-anchor") do not always
  // have pathname on IE
  (destination.pathname === '' ||
    // Don't catch links pointed to the same page but with a hash.
    destination.pathname === origin.pathname);

export const createClickHandler = (handleClick) => (event) => {
  // The click was already handled somehow.
  if (event.defaultPrevented) {
    return;
  }
  // Don't catch right-clicks, Ctrl-clicks, Cmd-clicks, etc.
  if (isModifiedClick(event)) {
    return;
  }
  // We might have clicked on a span within a link...
  const clickedAnchor = findClosestAnchor(event.target);
  // ...or not clicked inside a link at all.
  if (!clickedAnchor) {
    return;
  }
  // Certain links are not SPA links.
  if (isAuthorForcingNavigation(clickedAnchor)) {
    return;
  }

  // IE clears the host value if the anchor href changed after creation, e.g.
  // in React. Creating a new anchor element to ensure host value is present
  const destination = document.createElement('a');

  // https://html.spec.whatwg.org/multipage/links.html#concept-hyperlink-url-set
  // If clickedAnchor has no href attribute like `<a>example</a>`, the href getter returns empty string.
  if (clickedAnchor.href !== '') {
    destination.href = clickedAnchor.href;
  }

  if (
    'SVGAnimatedString' in window &&
    clickedAnchor.href instanceof SVGAnimatedString
  ) {
    destination.href = clickedAnchor.href.animVal;
  }

  // In IE, the default port is included in the anchor host but excluded from
  // the location host.  This affects the ability to directly compare
  // location host to anchor host.  For example: http://example.com would
  // have a location.host of 'example.com' and an destination.host of
  // 'example.com:80' Creating anchor from the location.href to normalize the
  // host value.
  const origin = document.createElement('a');
  origin.href = window.location.href;

  if (!urlsAreOnSameOrigin(origin, destination)) {
    return;
  }

  if (hashShouldBeFollowed(origin, destination)) {
    return;
  }

  const pathname = slashedPathname(destination.pathname);

  handleClick(event, {
    pathname,
    search: destination.search,
    hash: destination.hash,
    href: `${pathname}${destination.search}${destination.hash}`,
  });
};
